import { createFileRoute } from '@tanstack/react-router';

import { GoogleAnalyticsIntegrations } from '@mint-sdk/api-integrations';
import {
  AdserverUrnEnum,
  TRAFFICKING_PLATFORM_URN,
} from '@mint-sdk/api-integrations/compat';
import { AdAccount, OmniAuthAccounts } from '@mint-sdk/api-omni';
import { Integrations, Utils } from '@mint-sdk/api-omni-trafficking';
import { z } from 'zod';

export const Route = createFileRoute(
  '/_secured/external-data/integrations/$integration/connections/$connection/update',
)({
  validateSearch: z.object({
    tab: z.string().optional(),
    state: z.string().optional(),
    code: z.string().optional(),
    oauth_result: z.string().optional(),
  }),
  loader: async ({ context, params }) => {
    const { integration, connection } = params;
    const isAdserver = Utils.getAdServerPlatformFrom(integration);

    const isGA4 = integration === TRAFFICKING_PLATFORM_URN.googleAnalytics;

    const reservedPlatforms = [
      'amazon_integrated',
      'x_ads_integrated',
      'bing_integrated',
      'trade_desk_integrated',
    ];

    const queryClient =
      await context.serviceLocator.getOrThrowInstance('queryClient');

    if (isAdserver) {
      queryClient.ensureQueryData(
        Integrations.getIntegrationList({
          params: {
            adServer: AdserverUrnEnum.safeParse(integration).data ?? null,
          },
        }),
      );
      return;
    }
    if (isGA4) {
      queryClient.ensureQueryData(
        GoogleAnalyticsIntegrations.getGA4Integrations({}),
      );
      return;
    }

    if (!isGA4 && !isAdserver) {
      queryClient.ensureQueryData(
        AdAccount.getList({
          params: { connectionId: connection, platform: integration },
        }),
      );
    }

    const connectionIsAuthorized = isConnectionAuthorized(connection);
    if (!connectionIsAuthorized) return;

    if (
      !reservedPlatforms.includes(integration) &&
      !Utils.getAdServerPlatformFrom(integration)
    ) {
      queryClient.ensureQueryData(
        OmniAuthAccounts.getOmniAuthAccounts({
          params: { connectionId: connection },
        }),
      );
      return;
    }

    if (integration === 'bing_integrated') {
      queryClient.ensureQueryData(
        OmniAuthAccounts.getOmniAuthMicrosoftAdvertisingAccounts({
          params: { connectionId: connection },
        }),
      );
      return;
    }

    if (integration === 'trade_desk_integrated') {
      queryClient.ensureQueryData(
        OmniAuthAccounts.getOmniAuthTradeDeskAccounts({
          params: { connectionId: connection },
        }),
      );
      return;
    }

    if (integration === 'x_ads_integrated') {
      queryClient.ensureQueryData(
        OmniAuthAccounts.getOmniAuthOAuth1Accounts({
          params: { connectionId: connection },
        }),
      );
      return;
    }

    if (integration === 'amazon_integrated') {
      queryClient.ensureQueryData(
        OmniAuthAccounts.getOmniAuthAmazonAccounts({
          params: { connectionId: connection },
        }),
      );
    }
  },
});

const getAuthorizedConnections = () => {
  try {
    const connections = sessionStorage.getItem('authorizedConnections');
    return connections ? JSON.parse(connections) : [];
  } catch {
    return [];
  }
};

const isConnectionAuthorized = (connection: string) => {
  if (!connection) return false;

  const connections = getAuthorizedConnections();
  return connections.includes(connection);
};
